import type { Metadata } from "next"
import { generateSEOMetadata } from "./seo"
import keywordsData from "@/data/keywords.json"

export const siteMetadata = {
  title: "Real Metro Metal | Leading Industrial Hardware Supplier in Colombo, Sri Lanka",
  description:
    "Trusted wholesale partner for high-quality industrial hardware in Sri Lanka. Supplying valves, pipes, structural steel, H-iron, boxbars, and GI conduits. Competitive pricing and reliable delivery across Colombo and island-wide.",
  keywords: [
    "industrial hardware Sri Lanka",
    "wholesale metal products Colombo",
    "steel suppliers Sri Lanka",
    "valves and fittings Colombo",
    "construction materials Sri Lanka",
    "H-iron boxbars nuts bolts",
    "GI conduits supplier",
    "metal fabrication supplies",
    "industrial equipment Colombo",
    "Real Metro Metal",
    "high quality hardware Sri Lanka",
    ...keywordsData.global,
    ...keywordsData.products
  ].join(", "),
  url: "https://realmetrometal.com",
  image: "/logo.png",
  siteName: "Real Metro Metal",
  locale: "en_US",
  type: "website",
}

export const businessInfo = {
  name: "Real Metro Metal",
  legalName: "Real Metro Metal (Private) Limited",
  image: siteMetadata.image,
  address: {
    "@type": "PostalAddress",
    streetAddress: "392 1/3 Old Moor St",
    addressLocality: "Colombo",
    addressRegion: "Western Province",
    postalCode: "01200",
    addressCountry: "LK",
  },
  telephone: "+94 77 393 3643",
  whatsapp: "+94 75 393 3643",
  landline: "+94 11 239 0293",
  email: "realmetrometal@gmail.com",
  url: siteMetadata.url,
  foundingDate: "2010",
  numberOfEmployees: "25-50",
  vatID: "LK-VAT-123456789",
  duns: "123456789",
  naics: "423510", // Metal Service Centers
  sic: "5051", // Metals Service Centers
}

export function generateMetadata(
  title?: string,
  description?: string,
  keywords?: string,
  image?: string,
  type: "website" | "article" = "website",
): Metadata {
  const keywordArray = keywords ? keywords.split(", ") : []

  return generateSEOMetadata({
    title,
    description,
    keywords: keywordArray,
    image,
    type,
  })
}

// Page-specific metadata generators
export const pageMetadata = {
  home: generateMetadata(
    "Real Metro Metal | Steel & Metal Supplier in Sri Lanka",
    "Reliable steel and metal supplier in Sri Lanka. We provide stainless steel pipes, tubes, and fittings with competitive prices.",
    "steel supplier Sri Lanka, stainless steel pipes Sri Lanka, metal supplier Colombo, steel fittings Sri Lanka",
  ),

  products: generateMetadata(
    "Metal Products Supplier in Sri Lanka | Real Metro Metal",
    "Leading supplier of metal products in Sri Lanka. We provide steel, aluminum, and custom metal solutions for various industries.",
    "metal products supplier Sri Lanka, steel products Colombo, industrial metal solutions, Real Metro Metal products",
  ),

  categories: generateMetadata(
    "Steel Fittings Supplier in Sri Lanka | Pipe Fittings & Accessories",
    "Find high-quality steel pipe fittings in Sri Lanka. We supply elbows, reducers, and connectors for industrial applications.",
    "steel fittings Sri Lanka, pipe fittings Colombo, industrial connectors, steel accessories Sri Lanka",
  ),

  about: generateMetadata(
    "About Real Metro Metal | Trusted Hardware Partner since 2010",
    "Real Metro Metal stands as a pillar of reliability in the Sri Lankan hardware industry, serving construction and industrial sectors with distinction. Located in the heart of Colombo's hardware hub at Old Moor Street, we focus on sourcing premium metal products that meet rigorous safety standards.",
    "about Real Metro Metal, hardware supplier history, Colombo metal industry, trusted hardware partner, industrial hardware experience",
  ),

  contact: generateMetadata(
    "Contact Us | Get Industrial Hardware Quotes in Colombo",
    "Get in touch with Real Metro Metal for competitive wholesale quotes on all your industrial hardware needs. Visit our showroom at Old Moor Street, Colombo 12, or reach out via phone and WhatsApp for immediate assistance.",
    "contact Real Metro Metal, hardware quotes Sri Lanka, Colombo hardware supplier contact, industrial hardware inquiry",
  ),

  privacy: generateMetadata(
    "Privacy Policy | Real Metro Metal Data Protection",
    "Read the Real Metro Metal privacy policy to understand how we handle your business data and personal information with utmost security. We are committed to protecting the privacy of our wholesale clients and website visitors through robust data management practices. This document outlines our information collection methods, usage transparency, and the rights you hold regarding your data when interacting with our industrial hardware supply services.",
    "privacy policy, data protection, personal information security, business data privacy, Real Metro Metal privacy, hardware supplier legal, data usage transparency",
  ),

  terms: generateMetadata(
    "Terms of Service | Wholesale Hardware Supply Conditions",
    "Review the Real Metro Metal terms of service governing our wholesale hardware distribution and supply agreements across Sri Lanka. These terms define the legal framework for our business interactions, including order processing, delivery liabilities, and payment conditions for industrial materials. By partnering with us, you agree to these standardized industry practices that ensure fair trade and high-quality service delivery for all our professional hardware clients.",
    "terms of service, terms and conditions, wholesale hardware terms, business conditions, Real Metro Metal terms, supply agreement, industrial hardware distribution legal",
  ),
}
